import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, Download, Printer, Search } from 'lucide-react'
import { getGeneralLedgerGaap } from '../../api/accounting'
import AccountSelect from '../../components/AccountSelect'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { StatusBadge } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money } from '../sales/salesShared'
import { PeriodPicker, ReportsTabs, StatementHead, acct, downloadCsv, periodLabel, useCompanyName, usePeriodParams } from './reportsShared'
import { printPage, useAutoPrint } from '../../utils/print'

const amount = (v) => (Math.abs(Number(v) || 0) < 0.005 ? '' : money(v))

// General ledger: every posting to each account in a period, with the
// balance brought forward, a running balance and the closing balance.
export default function GeneralLedger() {
  const company = useCompanyName()
  const { params, set, period, start, end, setPeriod } = usePeriodParams('this_month')
  // Older links name the account as "parent".
  const account = params.get('account') || params.get('parent') || ''
  const [data, setData] = useState(null)
  useAutoPrint(!!data)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState({})
  const [openAll, setOpenAll] = useState(!!account)

  useEffect(() => {
    if (!start || !end) return
    setError('')
    setData(null)
    setOpen({})
    getGeneralLedgerGaap({ start, end, account })
      .then(setData)
      .catch((e) => setError(e.message || 'Could not load the general ledger.'))
  }, [start, end, account])

  const accounts = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!data) return []
    if (!term) return data.accounts
    return data.accounts
      .map((a) => {
        if (`${a.code} ${a.name}`.toLowerCase().includes(term)) return a
        const lines = a.lines.filter((l) => `${docNo('je', l.je_no)} ${l.description} ${l.party} ${l.source?.label}`.toLowerCase().includes(term))
        return lines.length ? { ...a, lines } : null
      })
      .filter(Boolean)
  }, [data, search])

  const isOpen = (code) => open[code] ?? (openAll || !!search.trim())
  const toggle = (code) => setOpen((o) => ({ ...o, [code]: !isOpen(code) }))

  const exportCsv = () => downloadCsv('general_ledger', [
    ['Account', 'Date', 'Entry', 'Source', 'Description', 'Party', 'Debit', 'Credit', 'Balance'],
    ...accounts.flatMap((a) => [
      [`${a.code} ${a.name}`, start, '', '', 'Opening balance', '', '', '', a.opening.toFixed(2)],
      ...a.lines.map((l) => ['', l.date, docNo('je', l.je_no), l.source?.label, l.description, l.party,
        l.dr ? l.dr.toFixed(2) : '', l.cr ? l.cr.toFixed(2) : '', l.balance.toFixed(2)]),
      ['', end, '', '', 'Closing balance', '', a.debits.toFixed(2), a.credits.toFixed(2), a.closing.toFixed(2)],
    ]),
  ])

  return (
    <div className="ci-page fs-page">
      <ReportsTabs />
      <div className="ci-header-row fs-noprint">
        <div className="ci-title-block">
          <div>
            <h1>General ledger <HelpTip k="generalLedger" /></h1>
            <div className="ci-title-meta">Every posting to each account, with running balances.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={exportCsv}><Download size={15} />Export</button>
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => printPage()}><Printer size={15} />Print</button>
        </div>
      </div>

      <div className="ci-toolbar fs-noprint">
        <PeriodPicker period={period} start={start} end={end} setPeriod={setPeriod} set={set} />
        <AccountSelect className="ci-select gl-account" value={account} onChange={(v) => set({ account: v, parent: '' })} placeholder="All accounts" aria-label="Account" />
        <label className="ci-search" aria-label="Search postings">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Entry, description or name…" />
        </label>
        <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => { setOpen({}); setOpenAll((v) => !v) }}>{openAll ? 'Hide postings' : 'Show postings'}</button>
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? (!error && <Spinner label="Preparing the general ledger…" />) : (
        <section className="fs-card">
          <StatementHead company={company} title="General ledger" sub={`For the period ${periodLabel(data.period.start, data.period.end)}`} />
          {!account && accounts === data.accounts && data.accounts.length > 0 && (
            <div className="fs-check fs-noprint">
              {Math.abs(data.totals.debits - data.totals.credits) < 0.005
                ? <StatusBadge label="Debits = credits" tone="good" />
                : <StatusBadge label={`Out by ${money(Math.abs(data.totals.debits - data.totals.credits))}`} tone="bad" />}
            </div>
          )}
          <table className="fs-table gl-table">
            <thead>
              <tr>
                <th className="gl-date hide-sm">Date</th>
                <th className="gl-entry hide-sm">Entry</th>
                <th>Description</th>
                <th className="num hide-sm">Debit</th>
                <th className="num hide-sm">Credit</th>
                <th className="num"><span className="label-with-help">Balance<HelpTip k="ledgerBalance" /></span></th>
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 && (
                <tr className="fs-note"><td colSpan="6">{search.trim() ? 'Nothing matches the search.' : 'Nothing posted in this period.'}</td></tr>
              )}
              {accounts.map((a) => {
                const shown = isOpen(a.code)
                return (
                  <Fragment key={a.code}>
                    <tr className="gl-account-row">
                      <td colSpan="3">
                        <button type="button" className="fs-toggle gl-toggle" aria-expanded={shown} onClick={() => toggle(a.code)}>
                          {shown ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                          {a.code} · {a.name}
                        </button>
                        <span className="fs-extra">{a.section}{a.lines.length ? ` · ${a.lines.length} ${a.lines.length === 1 ? 'posting' : 'postings'}` : ''}</span>
                      </td>
                      <td className="num hide-sm">{amount(a.debits)}</td>
                      <td className="num hide-sm">{amount(a.credits)}</td>
                      <td className="num">{acct(a.closing)}</td>
                    </tr>
                    {shown && (
                      <>
                        <tr className="fs-detail gl-edge">
                          <td className="gl-date hide-sm">{fmtDay(data.period.start)}</td>
                          <td className="hide-sm" />
                          <td>Opening balance</td>
                          <td className="num hide-sm" /><td className="num hide-sm" />
                          <td className="num">{acct(a.opening)}</td>
                        </tr>
                        {a.lines.map((l, i) => (
                          <tr key={`${l.je_id}-${i}`} className="fs-detail">
                            <td className="gl-date hide-sm">{fmtDay(l.date)}</td>
                            <td className="hide-sm"><Link className="fs-link" to={`/accounting/journal/${l.je_id}`}>{docNo('je', l.je_no) || l.je_id}</Link></td>
                            <td>
                              {l.description || '—'}
                              <span className="cell-sub">
                                {l.source?.link ? <Link className="fs-link" to={l.source.link}>{l.source.label}</Link> : l.source?.label}
                                {l.party ? ` · ${l.party}` : ''}
                                <span className="show-sm">
                                  {' · '}{fmtDay(l.date)}{l.dr ? ` · Debit ${money(l.dr)}` : ''}{l.cr ? ` · Credit ${money(l.cr)}` : ''}
                                  {' · '}<Link className="fs-link" to={`/accounting/journal/${l.je_id}`}>{docNo('je', l.je_no) || l.je_id}</Link>
                                </span>
                              </span>
                            </td>
                            <td className="num hide-sm">{amount(l.dr)}</td>
                            <td className="num hide-sm">{amount(l.cr)}</td>
                            <td className="num">{acct(l.balance)}</td>
                          </tr>
                        ))}
                        <tr className="fs-subtotal gl-edge">
                          <td className="gl-date hide-sm">{fmtDay(data.period.end)}</td>
                          <td className="hide-sm" />
                          <td>Closing balance<span className="cell-sub show-sm">Debits {money(a.debits)} · Credits {money(a.credits)}</span></td>
                          <td className="num hide-sm">{money(a.debits)}</td>
                          <td className="num hide-sm">{money(a.credits)}</td>
                          <td className="num">{acct(a.closing)}</td>
                        </tr>
                      </>
                    )}
                  </Fragment>
                )
              })}
              {accounts.length > 0 && (
                <tr className="fs-grand">
                  <td colSpan="3">Total postings<span className="cell-sub show-sm">Debits {money(accounts.reduce((s, a) => s + a.debits, 0))} · Credits {money(accounts.reduce((s, a) => s + a.credits, 0))}</span></td>
                  <td className="num hide-sm">{money(accounts.reduce((s, a) => s + a.debits, 0))}</td>
                  <td className="num hide-sm">{money(accounts.reduce((s, a) => s + a.credits, 0))}</td>
                  <td className="num" />
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}
